import { lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd/lib';
import { FilterValue } from 'antd/lib/table/interface';
import { IUnitTestProps } from '@monorepo/client-types';
import {
  ColumnsType,
  Flex,
  Table,
  TablePaginationConfig,
} from '@monorepo/react-components';
import { AffiliationCommission, Payout } from '@monorepo/types';
import { Filters, TransactionData, TransactionTabType } from '../../types';
import DashboardCard from '../dashboard-card';
import EmptyState from '../empty-state';

import styles from './dashboard-table.module.scss';

const EmptyTransactionSvg = lazy(
  () => import('../../svg/empty-transaction.svg?react'),
);

type TableDataType = Payout | AffiliationCommission;

interface DashboardTableProps<T> extends IUnitTestProps {
  columns: ColumnsType<T>;
  transactionData: TransactionData<T>;
  tabType?: TransactionTabType;
  onFilter?: (filters: Filters) => void;
}

export function DashboardTable<T extends TableDataType>({
  columns,
  transactionData,
  tabType,
  onFilter,
  testId,
}: DashboardTableProps<T>) {
  const { t } = useTranslation('affiliate');

  const { data, isFetching, isError, totalItems, page, limit, setPage } =
    transactionData;

  const handleOnChange = (
    pagination: TablePaginationConfig,
    antdTableFilters: Record<string, FilterValue | null>,
  ) => {
    if (pagination.current) {
      setPage(pagination.current);
    }

    if (onFilter) {
      onFilter({
        ...antdTableFilters,
      });
    }
  };

  const generateUniqueId = (() => {
    let counter = 0;
    return (prefix = 'row') => `${prefix}-${counter++}-${Date.now()}`;
  })();

  const errorAlert = (
    <Alert
      message={t('affiliatePortal.error.error')}
      description={t('affiliatePortal.error.failed_to_load_affiliate_data')}
      type="error"
      showIcon
    />
  );

  return (
    <DashboardCard className={styles.dashboardTable} testId={testId}>
      <Flex vertical className={styles.tableWrapper}>
        {isError ? (
          errorAlert
        ) : (
          <div className={styles.tableContent}>
            <Table
              onChange={handleOnChange}
              size="small"
              loading={isFetching}
              pagination={{
                current: page,
                pageSize: limit,
                total: totalItems,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 30, 50],
                hideOnSinglePage: true,
              }}
              rowKey={() => generateUniqueId()}
              bordered={false}
              columns={columns}
              dataSource={data as T[]}
              scroll={{ x: true }}
              rowHoverable={false}
              locale={{
                emptyText: (
                  <EmptyState
                    SvgComponent={EmptyTransactionSvg}
                    tabType={tabType}
                    className={styles.emptyState}
                    iconClassName={styles.emptyStateIcon}
                  />
                ),
              }}
            />
          </div>
        )}
      </Flex>
    </DashboardCard>
  );
}
